<template>
  <el-dialog
    :model-value="visible"
    title="상품 상세"
    width="65%"
    destroy-on-close
    :close-on-click-modal="true"
    align-center
    class="detail-dialog"
    @close="handleClose"
  >
    <div v-if="loading" class="detail-loading">
      <el-icon class="is-loading"><Loading /></el-icon>
      <span>불러오는 중...</span>
    </div>
    <div v-else-if="error" class="detail-error">
      <el-icon><WarningFilled /></el-icon>
      <span>{{ error }}</span>
    </div>
    <div v-else-if="data" class="detail-content">
      <div class="detail-split">
        <div class="detail-left">
          <div class="detail-top-row">
            <div class="detail-image-col">
              <div class="detail-main-image">
                <img
                  v-if="data.parsedPayload?.mainImages?.length"
                  :src="data.parsedPayload.mainImages[selectedImageIndex] || data.parsedPayload.mainImages[0]"
                  alt="메인 이미지"
                  @error="handleImageError"
                />
                <div v-else class="detail-no-image">이미지 없음</div>
              </div>
              <div v-if="data.parsedPayload?.mainImages?.length > 1" class="detail-thumbnails">
                <div
                  v-for="(img, idx) in data.parsedPayload.mainImages"
                  :key="idx"
                  class="detail-thumb"
                  :class="{ active: selectedImageIndex === idx }"
                  @click="selectedImageIndex = idx"
                >
                  <img :src="img" :alt="`썸네일 ${idx + 1}`" @error="handleImageError" />
                </div>
              </div>
            </div>

            <div class="detail-info-col">
              <div class="detail-header">
                <el-tag :type="data.parsedPayload?.status === 'success' ? 'success' : 'danger'">
                  {{ data.parsedPayload?.status === 'success' ? '수집 성공' : '수집 실패' }}
                </el-tag>
                <el-tag v-if="data.parsedPayload?.source" type="info">
                  {{ data.parsedPayload.source }}
                </el-tag>
                <a
                  v-if="data.parsedPayload?.productUrl"
                  :href="data.parsedPayload.productUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="detail-link"
                >
                  원본 →
                </a>
              </div>

              <div class="detail-title-section">
                <h2 class="detail-title">{{ data.finalTitle || data.parsedPayload?.productName || '-' }}</h2>
                <p v-if="data.finalTitle && data.parsedPayload?.productName" class="detail-subtitle">
                  원본: {{ data.parsedPayload.productName }}
                </p>
                <div class="detail-price">{{ data.parsedPayload?.price || '-' }}원</div>
              </div>

              <div v-if="parsedTags?.length" class="detail-tags-section">
                <div class="detail-tags">
                  <el-tag v-for="tag in parsedTags" :key="tag" size="small">{{ tag }}</el-tag>
                </div>
              </div>

              <div v-if="parsedKeywords?.length" class="detail-keywords-section">
                <span class="detail-section-label">재생성 키워드</span>
                <div class="detail-keywords">
                  <el-tag v-for="kw in parsedKeywords" :key="kw" size="small" type="success">{{ kw }}</el-tag>
                </div>
              </div>
            </div>
          </div>

          <div v-if="data.parsedPayload?.options?.length" class="detail-options-section">
            <div class="detail-section-header">
              <span class="detail-section-label">옵션</span>
              <span class="detail-section-count">{{ data.parsedPayload.options.length }}개</span>
            </div>
            <div v-if="data.parsedPayload.optionGroupNames?.length" class="detail-option-groups">
              <el-tag v-for="name in data.parsedPayload.optionGroupNames" :key="name" size="small" type="info">{{ name }}</el-tag>
            </div>
            <div class="detail-options">
              <div v-for="(opt, idx) in data.parsedPayload.options" :key="idx" class="detail-option-item">
                <span class="option-names">{{ [opt.name1, opt.name2, opt.name3].filter(Boolean).join(' / ') || '-' }}</span>
                <span class="option-price">{{ opt.extraPrice || '0' }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="detail-right"
          @wheel.prevent="handleRightColumnWheel"
          @mousedown="startAutoScroll"
          @mouseup="stopAutoScroll"
          @mouseleave="stopAutoScroll"
          @contextmenu.prevent
        >
          <div v-if="data.hostedVideoUrl || data.parsedPayload?.video" class="detail-video-section">
            <h3 class="detail-section-title">동영상</h3>
            <a
              :href="data.hostedVideoUrl || data.parsedPayload.video"
              target="_blank"
              rel="noopener noreferrer"
              class="detail-video-link"
            >
              🎬 동영상 보기 →
            </a>
          </div>

          <div class="detail-html-section">
            <div class="detail-html-header">
              <h3 class="detail-section-title">상세 페이지</h3>
              <el-button
                size="small"
                :disabled="!data?.detailHtmlRaw && !data?.parsedPayload?.detailHtml"
                @click="toggleRawHtml"
              >
                {{ showRawHtml ? '가공본으로 전환' : '원본으로 전환' }}
              </el-button>
            </div>
            <div
              v-if="renderedDetailHtml"
              class="detail-html-content"
              v-html="renderedDetailHtml"
            />
            <el-empty v-else :description="showRawHtml ? '원본 HTML 없음' : '상세 HTML 없음'" :image-size="60" />
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <el-button @click="handleClose">닫기</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { Loading, WarningFilled } from '@element-plus/icons-vue';
import { fetchRegisterDetail } from '../../services/n2n';

export default {
  name: 'N2nProductDetailModal',
  components: {
    Loading,
    WarningFilled
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    productid: {
      type: Number,
      default: null
    }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const loading = ref(false);
    const error = ref(null);
    const data = ref(null);
    const selectedImageIndex = ref(0);
    const showRawHtml = ref(false);
    const autoScrollTimer = ref(null);

    const parseJsonField = value => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    const parsedTags = computed(() => {
      return parseJsonField(data.value?.parsedPayload?.tags);
    });

    const parsedKeywords = computed(() => {
      return parseJsonField(data.value?.regeneratedKeywords);
    });

    const renderedDetailHtml = computed(() => {
      if (showRawHtml.value) return data.value?.detailHtmlRaw || '';
      return data.value?.parsedPayload?.detailHtml || '';
    });

    const toggleRawHtml = () => {
      showRawHtml.value = !showRawHtml.value;
    };

    const loadDetail = async () => {
      if (!props.productid) return;
      loading.value = true;
      error.value = null;
      data.value = null;
      selectedImageIndex.value = 0;
      showRawHtml.value = false;
      try {
        const response = await fetchRegisterDetail(props.productid);
        data.value = response.data?.data ?? response.data;
      } catch (err) {
        error.value = err.response?.data?.message || '상세 정보를 불러오지 못했습니다.';
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      showRawHtml.value = false;
      stopAutoScroll();
      emit('update:visible', false);
    };

    const handleImageError = event => {
      event.target.src = '/no-image.jpg';
    };

    const handleRightColumnWheel = event => {
      const element = event.currentTarget;
      if (!(element instanceof HTMLElement)) return;
      element.scrollTop += event.deltaY * 4;
    };

    const stopAutoScroll = () => {
      if (!autoScrollTimer.value) return;
      window.clearInterval(autoScrollTimer.value);
      autoScrollTimer.value = null;
    };

    const startAutoScroll = event => {
      if (event.button !== 2 || autoScrollTimer.value) return;

      const element = event.currentTarget;
      if (!(element instanceof HTMLElement)) return;

      autoScrollTimer.value = window.setInterval(() => {
        const scrollBottom = element.scrollTop + element.clientHeight;
        if (scrollBottom >= element.scrollHeight) {
          stopAutoScroll();
          return;
        }
        element.scrollTop += 8;
      }, 16);
    };

    onBeforeUnmount(stopAutoScroll);

    watch(() => props.visible, newVal => {
      if (newVal && props.productid) {
        loadDetail();
      }
    }, { immediate: true });

    return {
      loading,
      error,
      data,
      selectedImageIndex,
      parsedTags,
      parsedKeywords,
      showRawHtml,
      renderedDetailHtml,
      toggleRawHtml,
      handleClose,
      handleImageError,
      handleRightColumnWheel,
      startAutoScroll,
      stopAutoScroll
    };
  }
};
</script>

<style scoped>
.detail-loading,
.detail-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.detail-loading .el-icon {
  font-size: 32px;
  color: var(--el-color-primary);
}

.detail-error .el-icon {
  font-size: 32px;
  color: var(--el-color-danger);
}

.detail-content {
  height: 70vh;
}

.detail-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: 100%;
}

.detail-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;
}

.detail-top-row {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 20px;
}

.detail-image-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-info-col {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;
  border-left: 1px solid var(--el-border-color-lighter);
  padding-left: 24px;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.detail-link {
  font-size: 13px;
  color: var(--el-color-primary);
  text-decoration: none;
  font-weight: 500;
  margin-left: auto;
}

.detail-link:hover {
  text-decoration: underline;
}

.detail-main-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--el-border-color-lighter);
  background: #f5f7fa;
}

.detail-main-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.detail-image-col .detail-main-image {
  max-width: 280px;
}

.detail-no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--el-text-color-placeholder);
  font-size: 14px;
}

.detail-thumbnails {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  max-width: 280px;
}

.detail-thumb {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #f5f7fa;
}

.detail-thumb.active {
  border-color: var(--el-color-primary);
}

.detail-thumb:hover {
  border-color: var(--el-color-primary-light-3);
}

.detail-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  line-height: 1.4;
}

.detail-subtitle {
  margin: 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
}

.detail-price {
  font-size: 20px;
  font-weight: 700;
  color: var(--el-color-primary);
}

.detail-tags-section,
.detail-keywords-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-section-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--el-text-color-secondary);
}

.detail-tags,
.detail-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-options-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--el-border-color-lighter);
}

.detail-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-section-count {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}

.detail-option-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.detail-option-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--el-fill-color-lighter);
  border-radius: 6px;
  font-size: 13px;
}

.option-names {
  color: var(--el-text-color-regular);
}

.option-price {
  font-weight: 600;
  color: var(--el-color-primary);
  white-space: nowrap;
}

.detail-video-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-html-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-html-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.detail-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.detail-video-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--el-fill-color-lighter);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  color: var(--el-color-primary);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.detail-video-link:hover {
  background: var(--el-color-primary-light-9);
  border-color: var(--el-color-primary);
}

.detail-html-content {
  background: #fff;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
  padding: 16px;
}

.detail-html-content :deep(img) {
  max-width: 100%;
  height: auto;
}


:deep(.detail-dialog .el-dialog__body) {
  padding: 20px;
}
</style>

